/*globals Promise, Symbol*/
import {
  DISABLE_ACTION_ITEMS,
  ENABLE_ACTION_ITEMS,
  ENABLE_PAGE_STATE_ASYNC,
  DISABLE_PAGE_STATE_ASYNC,
  MARK_PAGE_NOT_FOUND,
  MARK_PAGE_FOUND,
  MARK_REQUEST_TIMEOUT,
} from 'actions/PageStateActions';

import {
  MARK_USER_LOGGED_OUT,
} from 'actions/GlobalActions';

import { camelCaseKeys } from '../config/utils';
import { API_TIMEOUT } from '../config/variables';

let urlTimeoutId = {};

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
function callApi(endpoint) {
  return fetch(endpoint, {
    'credentials': 'same-origin',
  })
  .then(response => {
    if (urlTimeoutId[endpoint]) {
      clearTimeout(urlTimeoutId[endpoint]);
      delete urlTimeoutId[endpoint];
    }
    return response.json().then(json => ({ json, response })).catch(() => {
      if (response.ok) {
        return Promise.resolve({ response });
      }
      return Promise.reject({
        status: response.status,
      });
    });
  }).then(({ json, response }) => {
    if (Array.isArray(json)) {
      json = {
        data: json,
      };
    } else if(typeof json !== 'object') {
      json = {};
    }
    json.isServerOK = !!response.ok;
    const camelizedJson = camelCaseKeys(json);
    if (!camelizedJson.isServerOK) {
      return Promise.reject({
        status: response.status,
        ...camelizedJson,
      });
    }
    return { ...camelizedJson };
  });
}

function callApiWithTimeout({ endpoint, timeout }) {
  return Promise.race([
    callApi(endpoint),
    new Promise((resolve, reject) => {
      let id = setTimeout(() => {
        reject({
          status: 500,
          message: 'Request timed out',
        });
      }, timeout);
      urlTimeoutId[endpoint] = id;
    }),
  ]);
}

// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API = Symbol('Call API');

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default store => next => action => {
  const callAPI = action[CALL_API];
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  let { endpoint } = callAPI;
  const { types } = callAPI;

  if (typeof endpoint === 'function') {
    endpoint = endpoint(store.getState());
  }

  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL.');
  }
  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of three action types.');
  }
  if (!types.every(type => typeof type === 'string')) {
    throw new Error('Expected action types to be strings.');
  }

  const [requestType, successType, failureType] = types;

  function actionWith(data) {
    const finalAction = { ...action, ...data };
    delete finalAction[CALL_API];
    return finalAction;
  }

  function disableActionables () {
    next(actionWith({ type: ENABLE_PAGE_STATE_ASYNC }));
    next(actionWith({ type: DISABLE_ACTION_ITEMS }));
  }

  function enableActionables () {
    next(actionWith({ type: ENABLE_ACTION_ITEMS }));
    next(actionWith({ type: DISABLE_PAGE_STATE_ASYNC }));
  }

  function successHandler (response) {
    const { blocking = true } = callAPI;
    if (blocking) { enableActionables(); }
    next(actionWith({ type: MARK_PAGE_FOUND }));
    if(callAPI.successTypeActionProps) {
      next(actionWith({ response, ...callAPI.successTypeActionProps, type: successType }));
    } else {
      next(actionWith({ response, type: successType }));
    }
    const { onSuccess } = callAPI;
    if (onSuccess){
      if (typeof onSuccess !== 'function' ) {
        throw new Error('Success Callback should be a function');
      }
      onSuccess(response, store.getState(), store.dispatch);
    }
  }

  function handleCallApi () {
    const { blocking = true } = callAPI;
    if (blocking) { disableActionables(); }
    if (endpoint.indexOf('/webapi/') >= 0) {
      return callApiWithTimeout({ endpoint, timeout: API_TIMEOUT }).then(successHandler, failureHandler);
    }
    return callApi(endpoint).then(successHandler, failureHandler);
  }

  function failureHandler (response) {
    const { blocking = true } = callAPI;
    if (blocking) { enableActionables(); }

    response.status = response.status || 0;

    switch (response.status) {
      case 401:
      case 403:
        if (Object.keys(store.getState()).includes('dashboard')) {
          // 401 UnAuthorized or 403 forbidden redirects to be done only for logged in pages
          window.location.href = `${location.origin}/accounts/login/?next=${encodeURIComponent(window.location.pathname)}`;
          return false;
        }
        next(actionWith({ type: MARK_USER_LOGGED_OUT }));
        break;

      case 404:
        next(actionWith({ type: MARK_PAGE_NOT_FOUND }));
        break;
      case 0:
      case 500:
        next(actionWith({ type: MARK_REQUEST_TIMEOUT }));
        break;
    }

    next(actionWith({
      type: failureType,
      error: response.message || 'Something bad happened',
    }));

    const { onFailure } = callAPI;

    if (onFailure) {
      if (typeof onFailure !== 'function' ) {
        throw new Error('Failure Callback should be a function');
      }
      onFailure(response, store.getState(), store.dispatch);
    }
  }

  let requestTypeActionData = { type: requestType };

  if (callAPI.requestTypeActionProps) {
    requestTypeActionData = { ...requestTypeActionData, ...callAPI.requestTypeActionProps };
  }
  next(actionWith(requestTypeActionData));

  //validate the get params
  const { validator } = callAPI;
  let { toValidate } = callAPI;
  if (typeof validator === 'function' && typeof toValidate !== 'function') {
    throw new Error('Expected toValidate to be a function since you supplied a validator method');
  }

  if (!validator) {
    return handleCallApi();
  }

  if (typeof toValidate === 'function') {
    toValidate = toValidate(store.getState());
  }

  validator.validate(toValidate, (e, result) => {
    if (result.valid) {
      return handleCallApi();
    }

    let failureTypeActionData = {
      type: failureType,
      errors: result.errors,
    };

    if (callAPI.failureTypeActionProps) {
      failureTypeActionData = { ...failureTypeActionData, ...callAPI.failureTypeActionProps };
    }

    next(actionWith(failureTypeActionData));
  });
};



// WEBPACK FOOTER //
// ./src/common/middlewares/api.js